
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getTrackingData } from '../api/shipments';
import TrackingMap from './TrackingMap';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { TrackingData } from './types';
import TrackingPageFTL from './TrackingPageFTL';
import TrackingPagePTL from './TrackingPagePTL';
import { Button } from '../../../components/ui/button';
import * as HeroIcons from '@heroicons/react/24/outline';
import { CnDocumentModal } from './CnDocumentModal';
import { LhcModal } from './LhcModal';
import { NotesModal } from './NotesModal';

interface TrackingSettingsResponse {
  show_map: boolean;
}

// Mock function for tracking settings - replace with actual API call
const getTrackingSettings = async (): Promise<TrackingSettingsResponse> => {
  return { show_map: true };
};

const TrackingPage: React.FC = () => {
  const { cnNumber } = useParams<{ cnNumber: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumbs, setHeaderActions, setRightHeaderActions } = useBreadcrumbs();
  const [showMap, setShowMap] = useState(false);
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trackingSuccess, setTrackingSuccess] = useState(true);
  const [isDocModalOpen, setIsDocModalOpen] = useState<boolean>(false);
  const [isLhcModalOpen, setIsLhcModalOpen] = useState<boolean>(false);

  const refreshTrackingData = useCallback(async () => {
    if (!cnNumber) return;

    try {
      const response = await getTrackingData(cnNumber);
      if (response.success) {
        setTrackingData(response as TrackingData);
        setTrackingSuccess(true);
      } else {
        setTrackingSuccess(false);
      }
    } catch (error) {
      console.error('Error refreshing tracking data:', error);
    }
  }, [cnNumber]);

  // Set breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { label: 'Shipments', href: '#' },
      { label: 'In Transit', href: '/shipments/in-transit' },
      { label: `Track ${cnNumber || ''}` }
    ]);

    setHeaderActions(
      <HeroIcons.DocumentTextIcon
        className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-800"
        onClick={() => setIsDocModalOpen(true)}
      />
    );

    setRightHeaderActions(
      <>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3 py-1 text-xs bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700"
          onClick={() => setIsLhcModalOpen(true)}
        >
          <HeroIcons.CalendarDaysIcon className="h-4 w-4" />
        </Button>

        < NotesModal cnNumber={cnNumber ?? ""} />
      </>
    )

    return () => setHeaderActions(null);
  }, [setBreadcrumbs, cnNumber]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!cnNumber) {
        setError('CN Number is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch tracking data and settings in parallel
        const [trackingResponse, settingsResponse] = await Promise.allSettled([
          getTrackingData(cnNumber),
          getTrackingSettings()
        ]);

        if (isMounted) {
          // Handle tracking data
          if (trackingResponse.status === 'fulfilled') {
            const data = trackingResponse.value;
            if (data && data.success) {
              setTrackingData(data as TrackingData);
              setTrackingSuccess(true);
            } else {
              setTrackingSuccess(false);
            }
          } else {
            console.error('Error fetching tracking data:', trackingResponse.reason);
            setError('Failed to load tracking data');
          }

          // Handle settings
          const shouldShowMap = settingsResponse.status === 'fulfilled' &&
            settingsResponse.value &&
            Object.prototype.hasOwnProperty.call(settingsResponse.value, 'show_map') ?
            settingsResponse.value.show_map : false;

          setShowMap(shouldShowMap);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error in fetchData:', error);
          setError('Failed to load data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [cnNumber]);


  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50/30 absolute inset-0">
        <div className="flex justify-center space-x-1">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-red-600 mb-2">Error loading tracking data</div>
          <div className="text-sm text-gray-600">{error}</div>
          <button
            onClick={() => navigate(`/shipments/in-transit${location.search || ''}`)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Shipments
          </button>
        </div>
      </div>
    );
  }

  if (!trackingSuccess) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-gray-900 mb-2">Tracking Not Available</div>
          <div className="text-sm text-gray-600">Tracking information is not available for this shipment</div>
          <button
            onClick={() => navigate(`/shipments/in-transit${location.search || ''}`)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Shipments
          </button>
        </div>
      </div>
    );
  }

  if (!trackingData) {
    return null;
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-6 px-6 py-6 pb-6 overflow-hidden">
        <div className={showMap ? 'lg:w-[50%] w-full flex flex-col min-h-0' : 'w-full max-w-4xl mx-auto flex flex-col min-h-0'}>
          {trackingData.tracking_mode === 'ptl' ? (
            <TrackingPagePTL data={trackingData} />
          ) : (
            <TrackingPageFTL data={trackingData} />
          )}
        </div>

        {showMap && trackingSuccess && cnNumber && (
          <div className="lg:w-[50%] w-full min-h-0 flex flex-col">
            <TrackingMap cnNumber={cnNumber} onDataRefresh={refreshTrackingData} />
          </div>
        )}

        <CnDocumentModal
          isOpen={isDocModalOpen}
          onClose={() => setIsDocModalOpen(false)}
          cnNumber={cnNumber ?? ""}
        />

        <LhcModal
          isOpen={isLhcModalOpen}
          onClose={() => setIsLhcModalOpen(false)}
          cnNumber={cnNumber ?? ""}
          onSuccess={() => { }}
          onRefreshAlerts={() => { }}
        />
      </div>
    </div>
  );
};

export default TrackingPage;
